import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng';
import { SelectItem } from 'primeng/api';
import { VmeCommonModule } from '../../vme-common.module';
import { AlertPanelComponent } from '../alert-panel/alert-panel.component';
import { JobHoldService } from '../../services/job-hold.service';
import {
  IJobHoldReleaseWindow,
  IJobHoldSettings,
} from '../../interfaces/job-hold/job-hold-settings';
import { Message } from '../../classes/Message';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const NOTHING_SAVED = 'Unable to save changes. The problems are shown on the page.';

@Component({
  selector: 'job-hold-configuration',
  standalone: true,
  imports: [CommonModule, FormsModule, VmeCommonModule, AlertPanelComponent],
  templateUrl: './job-hold-configuration.component.html',
})
export class JobHoldConfigurationComponent implements OnInit {
  protected settings: IJobHoldSettings;
  protected isLoading = true;
  protected saving = false;
  protected showGuide = true;

  // Problems from the last save, keyed on the section or row they belong to.
  protected errors: Record<string, string[]> = {};

  // The settings as last loaded, in the shape they are saved in, to tell when something has changed.
  private savedSettings: string;

  // Monday first, matching the order the rows come back in and the way the spec table reads.
  protected readonly dayOptions: SelectItem[] = [1, 2, 3, 4, 5, 6, 0].map((day) => ({
    value: day,
    label: DAY_NAMES[day],
  }));

  constructor(
    private jobHoldService: JobHoldService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  protected get saveDisabledReason(): string {
    if (this.isLoading) {
      return 'Still loading';
    }

    if (this.saving) {
      return 'Saving in progress';
    }

    if (!this.settings) {
      return 'Settings could not be loaded';
    }

    if (!this.hasChanges) {
      return 'No changes to save';
    }

    return null;
  }

  async ngOnInit() {
    await this.load();
  }

  protected dayName(day: number) {
    return DAY_NAMES[day];
  }

  protected rowKey(day: number) {
    return `releaseWindow.${DAY_NAMES[day]}`;
  }

  // Every row problem, Monday first, shown together under the table.
  protected get windowErrors() {
    const problems = [...(this.errors.releaseWindows ?? [])];

    this.dayOptions.forEach((option) =>
      problems.push(...(this.errors[this.rowKey(option.value)] ?? [])),
    );

    return problems.length ? problems : null;
  }

  // Editing a field clears the problems shown against it, and the page ones, which could be about
  // any field. The rest stay until the next save.
  protected clearErrors(key: string) {
    delete this.errors[key];
    delete this.errors.page;
  }

  protected async load() {
    this.isLoading = true;

    try {
      const settings = await this.jobHoldService.getSettings();

      this.settings = this.convertTimes(settings, (value) => this.toDate(value));
      this.savedSettings = this.snapshot();
    } catch {
      this.settings = null;
      this.messageService.add(
        Message.Error('Could not load the job hold settings. Please try again.'),
      );
    } finally {
      this.isLoading = false;
    }
  }

  protected confirmSave() {
    this.errors = {};

    if (this.hasEmptyValues()) {
      this.messageService.add(Message.Error(NOTHING_SAVED));
      return;
    }

    this.confirmationService.confirm({
      key: 'job-hold-save',
      message:
        'Save the job hold settings? New jobs use them straight away. Jobs already held keep their release time unless their collection time changes.',
      accept: () => this.save(),
    });
  }

  private async save() {
    this.saving = true;

    try {
      await this.jobHoldService.saveSettings(
        this.convertTimes(this.settings, (value) => this.toTime(value)),
      );

      this.messageService.add(Message.Success('Job hold settings saved.'));
      await this.load();
    } catch (error: any) {
      const problems = error?.error?.validationErrors ?? [];

      problems.forEach((problem) => this.addError(problem.identifier, problem.errorMessage));

      this.messageService.add(
        Message.Error(
          problems.length
            ? NOTHING_SAVED
            : 'Could not save the job hold settings. Please try again.',
        ),
      );
    } finally {
      this.saving = false;
    }
  }

  // Time of day only: a typed time lands on today's date, a loaded one on 1 January 1970.
  protected windowLength(window: IJobHoldReleaseWindow) {
    const start = this.toDate(window.startTime);
    const end = this.toDate(window.endTime);

    if (!start || !end) {
      return '';
    }

    const minutes =
      end.getHours() * 60 + end.getMinutes() - (start.getHours() * 60 + start.getMinutes());

    return minutes > 0 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : '';
  }

  private get hasChanges() {
    return this.snapshot() !== this.savedSettings;
  }

  private snapshot() {
    return JSON.stringify(this.convertTimes(this.settings, (value) => this.toTime(value)));
  }

  // Every time of day field, listed once so loading and saving stay in step.
  private convertTimes(
    settings: IJobHoldSettings,
    convert: (value: string | Date) => string | Date,
  ): IJobHoldSettings {
    return {
      configuration: {
        ...settings.configuration,
        lateReleaseStartTime: convert(settings.configuration.lateReleaseStartTime),
        lateReleaseEndTime: convert(settings.configuration.lateReleaseEndTime),
        rescheduleLateReleaseStartTime: convert(
          settings.configuration.rescheduleLateReleaseStartTime,
        ),
        rescheduleLateReleaseEndTime: convert(settings.configuration.rescheduleLateReleaseEndTime),
        overnightCutoffTime: convert(settings.configuration.overnightCutoffTime),
        overnightResumeStartTime: convert(settings.configuration.overnightResumeStartTime),
        overnightResumeEndTime: convert(settings.configuration.overnightResumeEndTime),
      },
      releaseWindows: settings.releaseWindows.map((window) => ({
        ...window,
        startTime: convert(window.startTime),
        endTime: convert(window.endTime),
      })),
    };
  }

  private toDate(value: string | Date): Date {
    if (value == null) {
      return null;
    }

    return value instanceof Date ? value : new Date('1970-01-01T' + value);
  }

  private toTime(value: string | Date): string {
    if (value == null) {
      return null;
    }

    return value instanceof Date ? value.toTimeString().substring(0, 8) : value;
  }

  // The time picker empties a time it cannot read, and a cleared box would otherwise reach the
  // server as a value it cannot read either, coming back as a generic error.
  private hasEmptyValues() {
    this.settings.releaseWindows
      .filter((window) => !window.startTime || !window.endTime)
      .forEach((window) =>
        this.addError(
          this.rowKey(window.pickupDay),
          `The ${DAY_NAMES[window.pickupDay]} window needs a start and end time.`,
        ),
      );

    if (Object.values(this.settings.configuration).some((value) => value == null || value === '')) {
      this.addError(null, 'Every setting needs a value.');
    }

    return Object.keys(this.errors).length > 0;
  }

  private addError(identifier: string, message: string) {
    (this.errors[identifier || 'page'] ??= []).push(message);
  }
}
